import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, MapPin, User, CalendarBlank } from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();

export default function PublicTimelinePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    axios.get(`${API}/api/events/public/${slug}/timeline`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Could not load'))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <Centered><Spinner /></Centered>;
  if (err) return <Centered><p className="text-sm text-red-400">{err}</p></Centered>;

  const ev = data.event;
  const themeColor = ev.theme_color || '#FF6B35';
  const items = data.items || [];

  const isToday = (() => {
    if (!ev.event_date) return false;
    return ev.event_date.slice(0, 10) === new Date().toISOString().slice(0, 10);
  })();
  const nowHHMM = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  const currentIdx = isToday
    ? items.findIndex((it, i) => {
        const nxt = items[i + 1];
        return it.time <= nowHHMM && (!nxt || nxt.time > nowHHMM);
      })
    : -1;

  return (
    <div className="min-h-screen bg-neutral-950 text-white py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-3xl mb-2">{ev.theme_emoji || '🎉'}</div>
          <h1 className="text-2xl font-extrabold" style={{ color: themeColor }}>{ev.title}</h1>
          <p className="text-xs text-neutral-400 mt-2 flex items-center justify-center gap-3 flex-wrap">
            {ev.event_date && <span><CalendarBlank size={11} className="inline mr-0.5" />{ev.event_date}</span>}
            {ev.venue && <span><MapPin size={11} className="inline mr-0.5" />{ev.venue}</span>}
          </p>
        </div>

        {items.length === 0 ? (
          <p className="text-center text-sm text-neutral-500">No schedule published yet.</p>
        ) : (
          <div className="relative pl-12">
            <div className="absolute left-[42px] top-0 bottom-0 w-px bg-white/10" />
            {items.map((it, idx) => {
              const isCurrent = idx === currentIdx;
              return (
                <div key={it.item_id} className={`relative mb-5 ${isCurrent ? 'animate-pulse-slow' : ''}`}>
                  <div className={`absolute -left-[46px] top-1 flex items-center justify-center w-9 h-9 rounded-full text-[11px] font-bold ${
                    isCurrent ? 'text-white shadow-lg' : 'bg-white/5 text-neutral-300 border border-white/10'
                  }`}
                    style={isCurrent ? { background: themeColor, boxShadow: `0 0 18px ${themeColor}` } : {}}>
                    {it.time || '—'}
                  </div>
                  {isCurrent && (
                    <span className="absolute -left-[55px] top-2.5 text-[9px] font-bold uppercase tracking-wider rotate-[-90deg] origin-left"
                      style={{ color: themeColor }}>NOW</span>
                  )}
                  <div className={`rounded-2xl px-4 py-3 ${isCurrent ? 'border' : 'bg-white/[0.03] border border-white/5'}`}
                    style={isCurrent ? { background: `${themeColor}15`, borderColor: `${themeColor}40` } : {}}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {it.icon && <span className="text-lg">{it.icon}</span>}
                      <span className="text-sm font-bold">{it.title}</span>
                    </div>
                    {(it.location || it.lead_owner) && (
                      <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px] text-neutral-400">
                        {it.location && <span><MapPin size={10} className="inline mr-0.5" />{it.location}</span>}
                        {it.lead_owner && <span><User size={10} className="inline mr-0.5" />{it.lead_owner}</span>}
                      </div>
                    )}
                    {it.description && <p className="text-[11px] text-neutral-400 mt-1">{it.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-[10px] text-neutral-600 mt-10">Powered by HomeCommand</p>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white p-4">{children}</div>;
}

function Spinner() {
  return <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />;
}
